/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/TenantAnalytics.tsx
 * PURPOSE: Tenant Admin Analytics Dashboard — 4-tab (Overview, Labor Cost, Attendance, BHXH)
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + 8-Mental-Model + Agentic)
 * DATE: 2026-03-21
 * COMPLIANCE: SDLC 6.3.1 + Vietnamese Labor Law 2019 + BHXH 2025
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance + Rule 7: Performance SLAs
 *
 * @business-context
 * - Domain: Tenant Admin Module 7 — Analytics & Reports for WFM F&B/Service
 * - Impact: Revenue Critical — labor cost visibility, BHXH compliance, leave utilization
 * - Stakeholders: Tenant Admins, HR Managers, CFO (NQH Holdings)
 *
 * @technical-architecture
 * - Layer: Presentation Layer (React 18 + Recharts)
 * - Pattern: Tab navigation, KPI cards, date-range filter, CSV export
 * - Dependencies: Recharts, TanStack Query, apiClient
 *
 * @cross-references
 * - Backend: backend/app/api/v2/tenant_admin/analytics.py (4 endpoints)
 * - Sprint Plan: docs/04-build/02-Sprint-Plans/SPRINT-078L-TA-ANALYTICS-APPROVAL-PHASE2-LOCAL.md
 * - BR: docs/01-planning/01-requirements/04-attendance/BR-ATTN-005-BHXH-INTEGRATION.md
 */

import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuthStore } from "../../store/auth";
import apiClient, { apiClientV2 } from "../../services/api/client";

// ─── Types ───────────────────────────────────────────────────────────────

interface KPICard {
  label: string;
  value: string | number;
  sublabel?: string;
  color: string;
}

interface LaborCostData {
  period: string;
  total_labor_cost_vnd: number;
  total_ot_cost_vnd: number;
  total_late_penalties_vnd: number;
  total_employees: number;
  by_branch: Array<{
    branch_name: string;
    employee_count: number;
    total_hours: number;
    ot_hours: number;
    cost_vnd: number;
  }>;
}

interface AttendanceData {
  period: string;
  total_employees: number;
  present_today: number;
  absent_today: number;
  late_today: number;
  attendance_rate_percent: number;
  by_day: Array<{
    date: string;
    present: number;
    absent: number;
    late: number;
  }>;
  top_late: Array<{
    employee_name: string;
    late_count: number;
    total_late_minutes: number;
  }>;
}

interface BHXHData {
  period: string;
  total_employees: number;
  total_employer_contribution_vnd: number;
  total_employee_contribution_vnd: number;
  salary_ceiling_vnd: number;
  by_type: {
    bhxh_175: number;
    bhyt_30: number;
    bhtn_10: number;
  };
  over_ceiling_count: number;
}

interface LeaveBalanceData {
  year: number;
  total_employees: number;
  avg_used_days: number;
  avg_remaining_days: number;
  by_type: Array<{
    leave_type: string;
    total_allocated: number;
    total_used: number;
    total_remaining: number;
  }>;
}

// ─── Tabs ────────────────────────────────────────────────────────────────

type TabId = "overview" | "labor-cost" | "attendance" | "bhxh";

const TABS: Array<{ id: TabId; label: string; icon: string }> = [
  { id: "overview", label: "Tổng quan", icon: "📊" },
  { id: "labor-cost", label: "Chi phí nhân công", icon: "💰" },
  { id: "attendance", label: "Chấm công", icon: "🕐" },
  { id: "bhxh", label: "Bảo hiểm xã hội", icon: "🛡️" },
];

// ─── Vietnamese Number Formatting ────────────────────────────────────────

function formatVND(amount: number): string {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("vi-VN").format(value);
}

// ─── Helper: Get current period ──────────────────────────────────────────

function getCurrentPeriod(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

// ─── KPI Card Component ─────────────────────────────────────────────────

const KPICardComponent: React.FC<KPICard> = ({ label, value, sublabel, color }) => (
  <div
    style={{
      background: "white",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      borderLeft: `4px solid ${color}`,
      flex: "1",
      minWidth: "200px",
    }}
  >
    <div style={{ fontSize: "14px", color: "#6b7280", marginBottom: "4px" }}>{label}</div>
    <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1f2937" }}>{value}</div>
    {sublabel && <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>{sublabel}</div>}
  </div>
);

// ─── Simple Bar Chart (no external dependency for basic rendering) ───────

const SimpleBarChart: React.FC<{
  data: Array<{ label: string; value: number }>;
  color: string;
  height?: number;
}> = ({ data, color, height = 200 }) => {
  const maxValue = Math.max(...data.map((d) => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "4px", height: `${height}px`, padding: "10px 0" }}>
      {data.map((item, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>
            {formatNumber(item.value)}
          </div>
          <div
            style={{
              width: "100%",
              maxWidth: "40px",
              height: `${(item.value / maxValue) * (height - 40)}px`,
              background: color,
              borderRadius: "4px 4px 0 0",
              minHeight: "4px",
            }}
          />
          <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px", textAlign: "center" }}>
            {item.label}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Overview Tab ────────────────────────────────────────────────────────

const OverviewTab: React.FC<{
  laborData: LaborCostData | null;
  attendanceData: AttendanceData | null;
  bhxhData: BHXHData | null;
  leaveData: LeaveBalanceData | null;
}> = ({ laborData, attendanceData, bhxhData, leaveData }) => {
  const kpis: KPICard[] = [
    {
      label: "Tổng nhân viên",
      value: formatNumber(attendanceData?.total_employees || 0),
      color: "#2563eb",
    },
    {
      label: "Tỷ lệ chấm công",
      value: formatPercent(attendanceData?.attendance_rate_percent || 0),
      sublabel: `Hôm nay: ${attendanceData?.present_today || 0} có mặt`,
      color: "#22c55e",
    },
    {
      label: "Chi phí nhân công",
      value: formatVND(laborData?.total_labor_cost_vnd || 0),
      sublabel: `Tăng ca: ${formatVND(laborData?.total_ot_cost_vnd || 0)}`,
      color: "#f59e0b",
    },
    {
      label: "BHXH doanh nghiệp",
      value: formatVND(bhxhData?.total_employer_contribution_vnd || 0),
      sublabel: `Trần lương: ${formatVND(bhxhData?.salary_ceiling_vnd || 46800000)}`,
      color: "#dc2626",
    },
  ];

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
        {kpis.map((kpi, i) => (
          <KPICardComponent key={i} {...kpi} />
        ))}
      </div>

      {/* Quick Stats Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        {/* Attendance Today */}
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#1f2937" }}>
            Chấm công hôm nay
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
            <div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#22c55e" }}>
                {attendanceData?.present_today || 0}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>Có mặt</div>
            </div>
            <div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#dc2626" }}>
                {attendanceData?.absent_today || 0}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>Vắng mặt</div>
            </div>
            <div>
              <div style={{ fontSize: "24px", fontWeight: "bold", color: "#f59e0b" }}>
                {attendanceData?.late_today || 0}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>Đi trễ</div>
            </div>
          </div>
        </div>

        {/* Leave Balance Summary */}
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px", color: "#1f2937" }}>
            Nghỉ phép năm
          </h3>
          {leaveData?.by_type.map((lt, i) => (
            <div key={i} style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", marginBottom: "4px" }}>
                <span style={{ color: "#6b7280" }}>
                  {lt.leave_type === "ANNUAL" ? "Nghỉ phép năm" : lt.leave_type}
                </span>
                <span style={{ fontWeight: "600" }}>
                  {lt.total_used}/{lt.total_allocated} ngày
                </span>
              </div>
              <div style={{ height: "8px", background: "#e5e7eb", borderRadius: "4px", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${Math.min(100, (lt.total_used / Math.max(lt.total_allocated, 1)) * 100)}%`,
                    height: "100%",
                    background: lt.total_remaining < 3 ? "#dc2626" : "#2563eb",
                    borderRadius: "4px",
                  }}
                />
              </div>
              <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>
                Còn lại: {lt.total_remaining} ngày
              </div>
            </div>
          ))}
          {(!leaveData || leaveData.by_type.length === 0) && (
            <div style={{ color: "#9ca3af", fontSize: "14px" }}>Chưa có dữ liệu nghỉ phép</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Labor Cost Tab ──────────────────────────────────────────────────────

const LaborCostTab: React.FC<{ data: LaborCostData | null }> = ({ data }) => {
  if (!data) return <div style={{ color: "#9ca3af", textAlign: "center", padding: "48px" }}>Đang tải...</div>;

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
        <KPICardComponent label="Tổng chi phí" value={formatVND(data.total_labor_cost_vnd)} color="#2563eb" />
        <KPICardComponent label="Chi phí tăng ca" value={formatVND(data.total_ot_cost_vnd)} sublabel={`${data.total_employees} nhân viên`} color="#f59e0b" />
        <KPICardComponent label="Phạt đi trễ" value={formatVND(data.total_late_penalties_vnd)} color="#dc2626" />
      </div>

      {/* Branch Breakdown Table */}
      <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>Chi phí theo chi nhánh</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Chi nhánh</th>
              <th style={{ textAlign: "right", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Nhân viên</th>
              <th style={{ textAlign: "right", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Tổng giờ</th>
              <th style={{ textAlign: "right", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Giờ TC</th>
              <th style={{ textAlign: "right", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Chi phí</th>
            </tr>
          </thead>
          <tbody>
            {data.by_branch.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                  Chưa có dữ liệu chi nhánh
                </td>
              </tr>
            ) : (
              data.by_branch.map((branch, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "10px 12px", fontSize: "14px" }}>{branch.branch_name}</td>
                  <td style={{ padding: "10px 12px", fontSize: "14px", textAlign: "right" }}>{branch.employee_count}</td>
                  <td style={{ padding: "10px 12px", fontSize: "14px", textAlign: "right" }}>{branch.total_hours.toFixed(1)}</td>
                  <td style={{ padding: "10px 12px", fontSize: "14px", textAlign: "right" }}>{branch.ot_hours.toFixed(1)}</td>
                  <td style={{ padding: "10px 12px", fontSize: "14px", textAlign: "right", fontWeight: "600" }}>{formatVND(branch.cost_vnd)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Attendance Tab ──────────────────────────────────────────────────────

const AttendanceTab: React.FC<{ data: AttendanceData | null }> = ({ data }) => {
  if (!data) return <div style={{ color: "#9ca3af", textAlign: "center", padding: "48px" }}>Đang tải...</div>;

  return (
    <div>
      {/* Summary */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
        <KPICardComponent label="Tỷ lệ chấm công" value={formatPercent(data.attendance_rate_percent)} color="#22c55e" />
        <KPICardComponent label="Có mặt hôm nay" value={data.present_today} sublabel={`/ ${data.total_employees} nhân viên`} color="#2563eb" />
        <KPICardComponent label="Đi trễ hôm nay" value={data.late_today} color="#f59e0b" />
        <KPICardComponent label="Vắng mặt" value={data.absent_today} color="#dc2626" />
      </div>

      {/* Top Late Employees */}
      <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
        <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>Nhân viên đi trễ nhiều nhất</h3>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Nhân viên</th>
              <th style={{ textAlign: "right", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Số lần trễ</th>
              <th style={{ textAlign: "right", padding: "8px 12px", fontSize: "14px", color: "#6b7280" }}>Tổng phút trễ</th>
            </tr>
          </thead>
          <tbody>
            {data.top_late.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                  Không có nhân viên đi trễ trong kỳ này
                </td>
              </tr>
            ) : (
              data.top_late.map((emp, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "10px 12px", fontSize: "14px" }}>{emp.employee_name}</td>
                  <td style={{ padding: "10px 12px", fontSize: "14px", textAlign: "right", color: "#f59e0b", fontWeight: "600" }}>{emp.late_count}</td>
                  <td style={{ padding: "10px 12px", fontSize: "14px", textAlign: "right" }}>{emp.total_late_minutes} phút</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── BHXH Tab ────────────────────────────────────────────────────────────

const BHXHTab: React.FC<{ data: BHXHData | null }> = ({ data }) => {
  if (!data) return <div style={{ color: "#9ca3af", textAlign: "center", padding: "48px" }}>Đang tải...</div>;

  const total = data.total_employer_contribution_vnd + data.total_employee_contribution_vnd;

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
        <KPICardComponent label="Doanh nghiệp đóng (21.5%)" value={formatVND(data.total_employer_contribution_vnd)} color="#2563eb" />
        <KPICardComponent label="Người lao động đóng (10.5%)" value={formatVND(data.total_employee_contribution_vnd)} color="#22c55e" />
        <KPICardComponent label="Tổng BHXH" value={formatVND(total)} sublabel={`${data.total_employees} nhân viên`} color="#7c3aed" />
      </div>

      {/* BHXH Breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>Chi tiết đóng góp (phía doanh nghiệp)</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[
              { label: "BHXH (17.5%)", value: data.by_type.bhxh_175, color: "#2563eb" },
              { label: "BHYT (3.0%)", value: data.by_type.bhyt_30, color: "#22c55e" },
              { label: "BHTN (1.0%)", value: data.by_type.bhtn_10, color: "#f59e0b" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: item.color }} />
                  <span style={{ fontSize: "14px", color: "#374151" }}>{item.label}</span>
                </div>
                <span style={{ fontSize: "14px", fontWeight: "600" }}>{formatVND(item.value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Ceiling Warning */}
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px" }}>Cảnh báo trần lương</h3>
          <div style={{ textAlign: "center", padding: "16px" }}>
            <div style={{ fontSize: "36px", fontWeight: "bold", color: data.over_ceiling_count > 0 ? "#dc2626" : "#22c55e" }}>
              {data.over_ceiling_count}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px" }}>
              Nhân viên vượt trần {formatVND(data.salary_ceiling_vnd)}
            </div>
            {data.over_ceiling_count > 0 && (
              <div style={{
                marginTop: "12px",
                padding: "8px 12px",
                background: "#fef2f2",
                borderRadius: "8px",
                fontSize: "13px",
                color: "#dc2626",
              }}>
                ⚠️ {data.over_ceiling_count} nhân viên có lương vượt trần BHXH. Phần vượt không tính đóng.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────

const TenantAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, userRole } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [period, setPeriod] = useState<string>(getCurrentPeriod());
  const [isLoading, setIsLoading] = useState(false);

  // Data state
  const [laborData, setLaborData] = useState<LaborCostData | null>(null);
  const [attendanceData, setAttendanceData] = useState<AttendanceData | null>(null);
  const [bhxhData, setBhxhData] = useState<BHXHData | null>(null);
  const [leaveData, setLeaveData] = useState<LeaveBalanceData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Auth guard
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  if (userRole !== "TENANT_ADMIN" && userRole !== "TENANT_OWNER") {
    return <Navigate to="/dashboard/staff" replace />;
  }

  // Fetch data
  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [laborRes, attendRes, bhxhRes, leaveRes] = await Promise.allSettled([
        apiClientV2.get(`/api/v2/tenant-admin/analytics/labor-cost?period=${period}`),
        apiClientV2.get(`/api/v2/tenant-admin/analytics/attendance?period=${period}`),
        apiClientV2.get(`/api/v2/tenant-admin/analytics/bhxh?period=${period}`),
        apiClientV2.get(`/api/v2/tenant-admin/analytics/leave-balance?year=${period.split("-")[0]}`),
      ]);

      if (laborRes.status === "fulfilled") setLaborData(laborRes.value.data);
      if (attendRes.status === "fulfilled") setAttendanceData(attendRes.value.data);
      if (bhxhRes.status === "fulfilled") setBhxhData(bhxhRes.value.data);
      if (leaveRes.status === "fulfilled") setLeaveData(leaveRes.value.data);
    } catch (err: any) {
      setError(err?.message || "Lỗi tải dữ liệu phân tích");
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  // Auto-fetch on mount and period change
  React.useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 60000); // Auto-refresh 60s
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  // Export CSV
  const handleExportCSV = useCallback(() => {
    if (!laborData) return;
    const headers = "Chi nhánh,Nhân viên,Tổng giờ,Giờ TC,Chi phí (VND)\n";
    const rows = laborData.by_branch
      .map((b) => `${b.branch_name},${b.employee_count},${b.total_hours},${b.ot_hours},${b.cost_vnd}`)
      .join("\n");
    const blob = new Blob(["\uFEFF" + headers + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bao-cao-nhan-cong-${period}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [laborData, period]);

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6" }}>
      {/* Header */}
      <nav style={{ background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", padding: "0 24px" }}>
        {/* 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout): bỏ maxWidth
            1200px + margin:auto — kéo sát lề khả dụng ở Desktop (khớp `main` bên dưới). */}
        <div style={{ display: "flex", alignItems: "center", height: "64px", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              onClick={() => navigate("/tenant-admin/dashboard")}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#4f46e5" }}
              aria-label="Quay lại trang quản trị"
            >
              &larr;
            </button>
            <h1 style={{ fontSize: "18px", fontWeight: "bold", color: "#1f2937", margin: 0 }}>
              Báo cáo & Phân tích
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {/* Period Selector */}
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              aria-label="Chọn kỳ báo cáo"
              style={{
                padding: "6px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
            {/* Export Button */}
            <button
              onClick={handleExportCSV}
              disabled={!laborData}
              style={{
                padding: "8px 16px",
                background: laborData ? "#2563eb" : "#9ca3af",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: laborData ? "pointer" : "not-allowed",
                fontSize: "14px",
              }}
            >
              Xuất báo cáo
            </button>
          </div>
        </div>
      </nav>

      <main style={{ padding: "24px" }}>
        {/* Tab Navigation */}
        <div style={{ display: "flex", gap: "4px", marginBottom: "24px", background: "white", borderRadius: "8px", padding: "4px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: "10px 16px",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: activeTab === tab.id ? "600" : "400",
                background: activeTab === tab.id ? "#2563eb" : "transparent",
                color: activeTab === tab.id ? "white" : "#6b7280",
                transition: "all 0.2s",
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Error State */}
        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", color: "#dc2626", fontSize: "14px" }}>
            ⚠️ {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>
            Đang tải dữ liệu...
          </div>
        )}

        {/* Tab Content */}
        {!isLoading && (
          <>
            {activeTab === "overview" && (
              <OverviewTab laborData={laborData} attendanceData={attendanceData} bhxhData={bhxhData} leaveData={leaveData} />
            )}
            {activeTab === "labor-cost" && <LaborCostTab data={laborData} />}
            {activeTab === "attendance" && <AttendanceTab data={attendanceData} />}
            {activeTab === "bhxh" && <BHXHTab data={bhxhData} />}
          </>
        )}
      </main>
    </div>
  );
};

export default TenantAnalytics;
